import { KeyboardEvent, MouseEvent, useState } from 'react';

import { EducationalInvestment, AppIcon } from '@backrs_ui/common-ui';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { EducationalInvestmentsModal } from './EducationalInvestmentsModal';

type EducationalInvestmentsCardProps = Readonly<{
  educationalInvestment?: { target: EducationalInvestment[]; others: EducationalInvestment[] };
}>;

export function EducationalInvestmentsCard({
  educationalInvestment,
}: EducationalInvestmentsCardProps) {
  const [modalInvestmentId, setModalInvestmentId] = useState<number | null>(null);

  if (!educationalInvestment) return null;

  const { target, others } = educationalInvestment;

  const handleClick = (e: MouseEvent | KeyboardEvent, id: number) => {
    e.stopPropagation();
    setModalInvestmentId(id);
  };

  return (
    <Box sx={containerSx}>
      {/* Recommended investments — inside a highlighted box */}
      {target.length > 0 && (
        <Box sx={targetBoxSx}>
          {target.map((inv) => (
            <Box
              key={inv.id}
              sx={itemSx}
              id={`education-card-target-${inv.id}`}
              onClick={(e) => handleClick(e, inv.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(e, inv.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <AppIcon iconName="Check" diameter="16px" color="Brand/600" />
              <Typography variant="text-xs-medium" sx={targetTextSx}>
                {inv.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Non-recommended investments — aligned with target text */}
      {others.map((inv) => (
        <Box
          key={inv.id}
          sx={otherItemSx}
          id={`education-card-other-${inv.id}`}
          onClick={(e) => handleClick(e, inv.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick(e, inv.id);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Box sx={placeholderIconSx} />
          <Typography variant="text-xs-medium" sx={otherTextSx}>
            {inv.name}
          </Typography>
        </Box>
      ))}

      <EducationalInvestmentsModal
        open={modalInvestmentId !== null}
        onClose={() => setModalInvestmentId(null)}
        target={target}
        others={others}
        initialExpandedId={modalInvestmentId}
      />
    </Box>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const targetBoxSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  padding: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Brand/500']}`,
  backgroundColor: backrs.colors['Brand/50'],
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  cursor: 'pointer',
  borderRadius: backrs.radius['radius-sm'],
  marginX: `-${backrs.spacing['spacing-xs']}`,
  paddingX: backrs.spacing['spacing-xs'],
  transition: 'opacity 0.1s ease-in-out',
  '&:hover': {
    opacity: 0.5,
  },
});

const targetTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/Black'],
});

const placeholderIconSx: SxProps = {
  width: '16px',
  flexShrink: 0,
};

const otherItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  cursor: 'pointer',
  borderRadius: backrs.radius['radius-sm'],
  marginX: `-${backrs.spacing['spacing-xs']}`,
  paddingX: backrs.spacing['spacing-xs'],
  paddingLeft: `calc(${backrs.spacing['spacing-md']} + ${backrs.spacing['spacing-xs']} + 1px)`,
  transition: 'opacity 0.1s ease-in-out',
  '&:hover': {
    opacity: 0.5,
  },
});

const otherTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/Black'],
});
